import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { User, Trophy, Vote, LayoutDashboard, Gavel, Gift } from 'lucide-react';

function Dashboard() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error('Failed to fetch user');
      }
      
      setUser(data);
    } catch (error) {
      toast.error('Failed to load user data');
      navigate('/login');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-12">
          <h1 data-testid="dashboard-welcome" className="text-5xl md:text-6xl font-bold mb-4 tracking-tight">
            Welcome, {user?.name}!
          </h1>
          <p className="text-xl text-muted-foreground">
            {user?.is_artist ? 'Showcase your talent to the world' : 'Support your favorite artists'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="glass-panel p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Free Votes</h3>
              <Vote className="h-8 w-8 text-secondary" />
            </div>
            <p className="text-4xl font-bold text-secondary">{user?.free_votes_remaining || 0}</p>
            <p className="text-sm text-muted-foreground mt-2">Remaining votes</p>
          </Card>

          <Card className="glass-panel p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Your Role</h3>
              <User className="h-8 w-8 text-primary" />
            </div>
            <p className="text-2xl font-bold">
              {user?.is_admin ? 'Admin' : user?.is_judge ? 'Judge' : user?.is_artist ? 'Artist' : 'Voter'}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {user?.stage_name || 'Enjoy the show'}
            </p>
          </Card>

          <Card className="glass-panel p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Quick Actions</h3>
              <Trophy className="h-8 w-8 text-accent" />
            </div>
            <div className="space-y-2">
              <Button
                size="sm"
                className="w-full bg-primary rounded-full"
                onClick={() => navigate('/voting')}
              >
                Vote Now
              </Button>
              <Button
                size="sm"
                className="w-full bg-secondary text-black rounded-full"
                onClick={() => navigate('/earn-votes')}
              >
                <Gift className="mr-2 h-4 w-4" />
                Earn Your Votes
              </Button>
              {(user?.is_admin || user?.is_judge) && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full rounded-full border-accent text-accent hover:bg-accent/10"
                  onClick={() => navigate('/judging-panel')}
                  data-testid="judging-panel-btn"
                >
                  <Gavel className="mr-2 h-4 w-4" />
                  Judging Panel
                </Button>
              )}
            </div>
          </Card>
        </div>

        {!user?.is_artist && (
          <Card className="glass-panel p-8 mb-12">
            <h2 className="text-3xl font-bold mb-4">Become an Artist</h2>
            <p className="text-muted-foreground mb-6">
              Register as an artist to showcase your talent, gain followers, and compete for prizes.
            </p>
            <Button
              className="bg-secondary hover:brightness-110 text-black rounded-full"
              onClick={() => navigate('/join-talent-show')}
            >
              Register as Artist
            </Button>
          </Card>
        )}

        <div>
          <h2 className="text-3xl font-bold mb-6">Explore</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/feed">
              <Card className="glass-panel p-6 hover:border-primary/50 transition-colors group cursor-pointer">
                <LayoutDashboard className="h-12 w-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-bold mb-2">Discover Feeds</h3>
                <p className="text-muted-foreground">See what artists are sharing</p>
              </Card>
            </Link>

            <Link to="/voting">
              <Card className="glass-panel p-6 hover:border-primary/50 transition-colors group cursor-pointer">
                <Vote className="h-12 w-12 text-secondary mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-bold mb-2">Cast Votes</h3>
                <p className="text-muted-foreground">Support your favorite artists</p>
              </Card>
            </Link>

            <Link to="/leaderboard">
              <Card className="glass-panel p-6 hover:border-primary/50 transition-colors group cursor-pointer">
                <Trophy className="h-12 w-12 text-accent mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-bold mb-2">Leaderboard</h3>
                <p className="text-muted-foreground">See who's leading the competition</p>
              </Card>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;